import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import RequestHistory from '../../components/RequestHistory';
import { 
  Send, Clock, CheckCircle, XCircle, RefreshCw, Loader2, 
  Plus, Eye, Inbox, Users, Building, Calculator, Settings,
  FileText, Calendar, Upload, X, File, Download, Bell, BellDot,
  History, ArrowRight, User
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth header helper
const getAuthHeaders = () => {
  const token = localStorage.getItem('token') || localStorage.getItem('fortis_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const WorkRequests = () => {
  const { user } = useAuth();
  const [categoryTab, setCategoryTab] = useState('ik');
  const [statusTab, setStatusTab] = useState('pending');
  
  // Requests state
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  
  // Modal states
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  
  // Form state
  const [newRequest, setNewRequest] = useState({
    category: 'ik',
    title: '',
    description: '',
    priority: 'normal',
    documents: [],
    expected_delivery_date: ''  // Teslim tarihi (satın alma için)
  });

  const categories = [
    { id: 'ik', label: 'İK TALEPLERİM', icon: Users, color: 'text-blue-600' },
    { id: 'satin_alma', label: 'SATIN ALMA TALEPLERİM', icon: Building, color: 'text-purple-600' },
    { id: 'muhasebe', label: 'MUHASEBE TALEPLERİM', icon: Calculator, color: 'text-green-600' },
    { id: 'idari_isler', label: 'İDARİ İŞLER TALEPLERİM', icon: Settings, color: 'text-orange-600' },
    { id: 'genel_talepler', label: 'GENEL TALEPLER', icon: FileText, color: 'text-gray-600' }
  ];

  // Kategorilere göre talep başlıkları
  const requestTitles = {
    ik: [
      'İzin Talebi',
      'Avans Talebi',
      'Maaş Bilgi Talebi',
      'Belge Talebi',
      'Diğer İK Talebi'
    ],
    satin_alma: [
      'Malzeme Talebi',
      'Ekipman Talebi',
      'Ofis Malzemesi Talebi',
      'Diğer Satın Alma Talebi'
    ],
    muhasebe: [
      'Fatura Bilgi Talebi',
      'Ödeme Talebi',
      'Masraf Talebi',
      'Diğer Muhasebe Talebi'
    ],
    idari_isler: [
      'Bakım/Onarım Talebi',
      'Temizlik Talebi',
      'Güvenlik Talebi',
      'Diğer İdari Talep'
    ],
    genel_talepler: [
      'Depo Ürün İsteği',
      'Genel Bilgi Talebi',
      'Diğer Talep'
    ]
  };

  const getCurrentTitles = () => {
    return requestTitles[newRequest.category] || [];
  };

  // Kategori değişince başlığı sıfırla
  const prevCategoryRef = React.useRef(newRequest.category);
  useEffect(() => {
    if (prevCategoryRef.current !== newRequest.category) {
      setNewRequest(prev => ({ ...prev, title: '' }));
      prevCategoryRef.current = newRequest.category;
    }
  }, [newRequest.category]);

  useEffect(() => {
    loadRequests();
    loadUnreadCount();
  }, [categoryTab]);

  const loadUnreadCount = async () => {
    try {
      const res = await axios.get(`${API}/work/requests/unread-count`, {
        headers: getAuthHeaders()
      });
      setUnreadCount(res.data?.unread_count || 0);
    } catch (error) {
      console.error('Okunmamış sayısı yüklenemedi:', error);
    }
  };

  const markAsRead = async (requestId) => {
    try {
      await axios.post(`${API}/work/requests/${requestId}/mark-read`, {}, {
        headers: getAuthHeaders()
      });
      loadUnreadCount();
      // Update local state
      setRequests(prev => prev.map(r => 
        r.id === requestId ? { ...r, read_by_owner: true } : r
      ));
    } catch (error) {
      console.error('Okundu işaretlenemedi:', error);
    }
  };

  const loadRequests = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/work/requests/my`, {
        params: { category: categoryTab },
        headers: getAuthHeaders()
      });
      setRequests(res.data || []);
    } catch (error) {
      console.error('Talepler yüklenirken hata:', error);
      toast.error('Talepler yüklenemedi');
    }
    setLoading(false);
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    
    files.forEach((file, index) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        setNewRequest(prev => {
          const updatedRequest = {
            ...prev,
            documents: [...prev.documents, {
              name: file.name,
              type: file.type,
              size: file.size,
              data: event.target.result
            }]
          };
          
          // Tüm kategorilerde ilk dosya yüklendiğinde dosya adını talep başlığı yap
          if (prev.documents.length === 0 && index === 0) {
            // Dosya uzantısını kaldır ve başlık olarak kullan
            const fileName = file.name.replace(/\.[^/.]+$/, '');
            updatedRequest.title = fileName;
          }
          
          return updatedRequest;
        });
      };
      reader.readAsDataURL(file);
    });
  };

  const removeDocument = (index) => {
    setNewRequest(prev => ({
      ...prev,
      documents: prev.documents.filter((_, i) => i !== index)
    }));
  };

  const handleCreateRequest = async () => {
    if (!newRequest.title) {
      toast.error('Talep başlığı gerekli');
      return;
    }
    
    if (newRequest.documents.length === 0) {
      toast.error('En az bir belge yüklemelisiniz');
      return;
    }
    
    try {
      // Kullanıcının seçtiği kategori değerini kullan (newRequest.category)
      await axios.post(`${API}/work/requests`, {
        ...newRequest
      }, { headers: getAuthHeaders() });
      toast.success('Talep oluşturuldu');
      setCreateModalOpen(false);
      setNewRequest({ category: newRequest.category, title: '', description: '', priority: 'normal', documents: [], expected_delivery_date: '' });
      loadRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Talep oluşturulamadı');
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { label: 'Bekliyor', color: 'bg-amber-100 text-amber-700', icon: Clock },
      pending_muhasebe: { label: 'Muhasebe\'de Bekliyor', color: 'bg-purple-100 text-purple-700', icon: Clock },
      approved: { label: 'Onaylandı', color: 'bg-green-100 text-green-700', icon: CheckCircle },
      completed: { label: 'Tamamlandı', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
      rejected: { label: 'Reddedildi', color: 'bg-red-100 text-red-700', icon: XCircle },
      // İletilen durumlar
      forwarded_to_hr: { label: 'İK\'da Bekliyor', color: 'bg-blue-100 text-blue-700', icon: Clock },
      pending_hr: { label: 'İK\'da Bekliyor', color: 'bg-blue-100 text-blue-700', icon: Clock },
      forwarded_to_accounting: { label: 'Muhasebe\'de Bekliyor', color: 'bg-purple-100 text-purple-700', icon: Clock },
      pending_accounting: { label: 'Muhasebe\'de Bekliyor', color: 'bg-purple-100 text-purple-700', icon: Clock },
      forwarded_to_purchasing: { label: 'Satın Alma\'da Bekliyor', color: 'bg-cyan-100 text-cyan-700', icon: Clock },
      pending_purchasing: { label: 'Satın Alma\'da Bekliyor', color: 'bg-cyan-100 text-cyan-700', icon: Clock },
      forwarded_to_admin: { label: 'İdari İşler\'de Bekliyor', color: 'bg-indigo-100 text-indigo-700', icon: Clock },
      pending_admin: { label: 'İdari İşler\'de Bekliyor', color: 'bg-indigo-100 text-indigo-700', icon: Clock },
      forwarded_to_management: { label: 'Yönetim\'de Bekliyor', color: 'bg-rose-100 text-rose-700', icon: Clock },
      pending_management: { label: 'Yönetim\'de Bekliyor', color: 'bg-rose-100 text-rose-700', icon: Clock },
      forwarded: { label: 'İletildi', color: 'bg-blue-100 text-blue-700', icon: Clock },
      converted_to_job: { label: 'İşleniyor', color: 'bg-indigo-100 text-indigo-700', icon: Clock }
    };
    const c = config[status] || config.pending;
    const Icon = c.icon;
    return (
      <Badge className={`${c.color} gap-1`}>
        <Icon className="h-3 w-3" />
        {c.label}
      </Badge>
    );
  };

  const getPriorityBadge = (priority) => {
    const config = {
      low: { label: 'Düşük', color: 'bg-gray-100 text-gray-600' },
      normal: { label: 'Normal', color: 'bg-blue-100 text-blue-600' },
      high: { label: 'Yüksek', color: 'bg-orange-100 text-orange-600' },
      urgent: { label: 'Acil', color: 'bg-red-100 text-red-600' }
    };
    const c = config[priority] || config.normal;
    return <Badge className={c.color}>{c.label}</Badge>;
  };

  const getCategoryLabel = (category) => {
    const cat = categories.find(c => c.id === category);
    return cat ? cat.label.replace(' TALEPLERİM', '') : category;
  };

  // Filter requests by status
  // "pending" artık: pending, forwarded_to_hr, forwarded_to_*, pending_* durumlarını içerir
  // Kullanıcı kendi talebinin durumunu takip edebilmeli
  // "approved" artık: approved, converted_to_job (işe dönüştürülmüş = işleniyor)
  const filterByStatus = (status) => {
    if (status === 'pending') {
      // Bekleyen: Henüz nihai karar verilmemiş tüm talepler
      const pendingStatuses = [
        'pending',
        'pending_muhasebe',
        'forwarded_to_hr', 'pending_hr',
        'forwarded_to_accounting', 'pending_accounting',
        'forwarded_to_purchasing', 'pending_purchasing',
        'forwarded_to_admin', 'pending_admin',
        'forwarded_to_management', 'pending_management',
        'forwarded',
        'converted_to_job' // İşe dönüştürülmüş ama henüz tamamlanmamış
      ];
      return requests.filter(r => pendingStatuses.includes(r.status));
    }
    if (status === 'approved') {
      // Onaylanan: approved ve completed statüsündeki talepler
      return requests.filter(r => r.status === 'approved' || r.status === 'completed');
    }
    return requests.filter(r => r.status === status);
  };

  const pendingRequests = filterByStatus('pending');
  const approvedRequests = filterByStatus('approved');
  const rejectedRequests = filterByStatus('rejected');

  const renderRequestTable = (filteredRequests) => {
    if (filteredRequests.length === 0) {
      return (
        <Card>
          <CardContent className="p-8 text-center">
            <Inbox className="h-16 w-16 mx-auto mb-4 text-gray-200" />
            <h3 className="text-xl font-bold mb-2">Talep Bulunmuyor</h3>
            <p className="text-gray-500">Bu kategoride talep yok</p>
          </CardContent>
        </Card>
      );
    }

    return (
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Form No</TableHead>
                <TableHead>İş Konusu</TableHead>
                <TableHead>Belge Adı</TableHead>
                <TableHead>Öncelik</TableHead>
                <TableHead>Durum</TableHead>
                <TableHead>Tarih</TableHead>
                <TableHead className="text-right">İşlemler</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredRequests.map(req => (
                <TableRow 
                  key={req.id} 
                  data-testid={`request-row-${req.id}`}
                  className={req.read_by_owner === false ? 'bg-blue-50 hover:bg-blue-100' : ''}
                >
                  <TableCell>
                    <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded text-gray-600">
                      {req.form_number || '-'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {req.read_by_owner === false && (
                        <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" title="Okunmamış"></div>
                      )}
                      <div>
                        <p className={`font-medium ${req.read_by_owner === false ? 'font-bold text-blue-700' : ''}`}>
                          {req.title}
                        </p>
                        {req.description && (
                          <p className="text-sm text-gray-500 truncate max-w-[200px]">{req.description}</p>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    {req.documents && req.documents.length > 0 ? (
                      <div className="flex items-center gap-1 text-sm">
                        <FileText className="h-4 w-4 text-amber-500" />
                        <span className="truncate max-w-[150px]">{req.documents[0].name}</span>
                        {req.documents.length > 1 && (
                          <span className="text-xs text-gray-400">+{req.documents.length - 1}</span>
                        )}
                      </div>
                    ) : (
                      <span className="text-gray-400 text-sm">-</span>
                    )}
                  </TableCell>
                  <TableCell>{getPriorityBadge(req.priority)}</TableCell>
                  <TableCell>{getStatusBadge(req.status)}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1 text-sm text-gray-500">
                      <Calendar className="h-4 w-4" />
                      {new Date(req.created_at).toLocaleDateString('tr-TR')}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => { 
                        setSelectedRequest(req); 
                        setDetailModalOpen(true);
                        // Okunmamışsa okundu olarak işaretle
                        if (req.read_by_owner === false) {
                          markAsRead(req.id);
                        }
                      }}
                      data-testid={`view-request-${req.id}`}
                    >
                      {req.read_by_owner === false && <BellDot className="h-4 w-4 mr-1 text-blue-500" />}
                      {req.read_by_owner !== false && <Eye className="h-4 w-4 mr-1" />}
                      Detay
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    );
  };

  const currentCategory = categories.find(c => c.id === categoryTab);
  const CategoryIcon = currentCategory?.icon || FileText;

  return (
    <div className="space-y-6" data-testid="work-requests">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">TALEPLERİM</h1>
          <p className="text-gray-500">İş taleplerinizi kategoriye göre yönetin</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={loadRequests} variant="outline" size="sm" className="gap-2">
            <RefreshCw className="h-4 w-4" />
            Yenile
          </Button>
          <Button 
            onClick={() => {
              setNewRequest(prev => ({ ...prev, category: categoryTab, title: '' }));
              setCreateModalOpen(true);
            }} 
            className="gap-2 bg-[hsl(var(--brand-700))] hover:bg-[hsl(var(--brand-800))]"
            data-testid="create-request-btn"
          >
            <Plus className="h-4 w-4" />
            Yeni Talep
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40">
          <Loader2 className="h-8 w-8 animate-spin text-[hsl(var(--brand-600))]" />
        </div>
      ) : (
        <Tabs value={categoryTab} onValueChange={setCategoryTab} className="w-full">
          <TabsList className="grid w-full grid-cols-5 mb-4">
            {categories.map(cat => {
              const Icon = cat.icon;
              const count = requests.filter(r => r.category === cat.id).length;
              return (
                <TabsTrigger 
                  key={cat.id} 
                  value={cat.id} 
                  className="gap-1 text-xs sm:text-sm"
                  data-testid={`category-tab-${cat.id}`}
                >
                  <Icon className={`h-4 w-4 ${cat.color}`} />
                  <span className="hidden sm:inline">{cat.label.replace(' TALEPLERİM', '')}</span>
                  <span className="sm:hidden">{cat.id === 'ik' ? 'İK' : cat.id === 'satin_alma' ? 'S.A.' : cat.id === 'muhasebe' ? 'MUH' : cat.id === 'idari_isler' ? 'İ.İ.' : 'G.T.'}</span>
                </TabsTrigger>
              );
            })}
          </TabsList>

          {categories.map(cat => (
            <TabsContent key={cat.id} value={cat.id} className="mt-4">
              <Card className="mb-4">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <cat.icon className={`h-5 w-5 ${cat.color}`} />
                    {cat.label}
                  </CardTitle>
                </CardHeader>
              </Card>

              <Tabs value={statusTab} onValueChange={setStatusTab}>
                <TabsList>
                  <TabsTrigger value="pending" className="gap-2" data-testid={`status-pending-${cat.id}`}>
                    <Clock className="h-4 w-4" />
                    BEKLEYEN TALEPLER
                    {pendingRequests.length > 0 && (
                      <Badge className="ml-1 bg-amber-100 text-amber-700">{pendingRequests.length}</Badge>
                    )}
                  </TabsTrigger>
                  <TabsTrigger value="approved" className="gap-2" data-testid={`status-approved-${cat.id}`}>
                    <CheckCircle className="h-4 w-4" />
                    ONAYLANAN TALEPLER
                    {approvedRequests.length > 0 && (
                      <Badge className="ml-1 bg-green-100 text-green-700">{approvedRequests.length}</Badge>
                    )}
                  </TabsTrigger>
                  <TabsTrigger value="rejected" className="gap-2" data-testid={`status-rejected-${cat.id}`}>
                    <XCircle className="h-4 w-4" />
                    RED EDİLEN TALEPLER
                    {rejectedRequests.length > 0 && (
                      <Badge className="ml-1 bg-red-100 text-red-700">{rejectedRequests.length}</Badge>
                    )}
                    {rejectedRequests.filter(r => r.read_by_owner === false).length > 0 && (
                      <Badge className="ml-1 bg-blue-500 text-white animate-pulse">
                        {rejectedRequests.filter(r => r.read_by_owner === false).length} yeni
                      </Badge>
                    )}
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="pending" className="mt-4">
                  {renderRequestTable(pendingRequests)}
                </TabsContent>
                <TabsContent value="approved" className="mt-4">
                  {renderRequestTable(approvedRequests)}
                </TabsContent>
                <TabsContent value="rejected" className="mt-4">
                  {renderRequestTable(rejectedRequests)}
                </TabsContent>
              </Tabs>
            </TabsContent>
          ))}
        </Tabs>
      )}

      {/* Talep Oluşturma Modalı */}
      <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5 text-[hsl(var(--brand-600))]" />
              Yeni Talep Oluştur
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="request-category">Kategori</Label>
              <Select 
                value={newRequest.category || categoryTab} 
                onValueChange={(val) => setNewRequest({ ...newRequest, category: val })}
              >
                <SelectTrigger data-testid="request-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {getCategoryLabel(cat.id)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Tüm kategorilerde talep başlığı dosya adından otomatik alınır */}
            <div>
              <Label htmlFor="request-title">İş Konusu</Label>
              <Input
                id="request-title"
                value={newRequest.title}
                readOnly
                placeholder="Dosya yüklendiğinde otomatik doldurulacak"
                className="bg-gray-50"
                data-testid="request-title-input"
              />
              <p className="text-xs text-gray-500 mt-1">Dosya adından otomatik alınır</p>
            </div>
            
            <div>
              <Label htmlFor="request-desc">Açıklama</Label>
              <Textarea 
                id="request-desc"
                value={newRequest.description}
                onChange={(e) => setNewRequest({ ...newRequest, description: e.target.value })}
                placeholder="Talep açıklaması"
                rows={3}
                data-testid="request-desc-input"
              />
            </div>
            
            <div>
              <Label htmlFor="request-priority">Öncelik</Label>
              <Select 
                value={newRequest.priority} 
                onValueChange={(val) => setNewRequest({ ...newRequest, priority: val })}
              >
                <SelectTrigger data-testid="request-priority-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Düşük</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="high">Yüksek</SelectItem>
                  <SelectItem value="urgent">Acil</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* İstek Zamanı - Tüm kategorilerde göster */}
            <div>
              <Label htmlFor="expected-delivery-date" className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                İstek Zamanı
              </Label>
              <Input
                id="expected-delivery-date"
                type="date"
                value={newRequest.expected_delivery_date}
                onChange={(e) => setNewRequest({ ...newRequest, expected_delivery_date: e.target.value })}
                className="mt-1"
                data-testid="expected-delivery-date-input"
              />
              <p className="text-xs text-gray-500 mt-1">Talebinizin ne zaman karşılanmasını istiyorsunuz?</p>
            </div>

            <div>
              <Label className="flex items-center gap-1">
                Belge Yükle *
                <span className="text-red-500 text-xs">(Zorunlu)</span>
              </Label>
              <div className="mt-2">
                <label className="flex flex-col items-center justify-center w-full h-24 border-2 border-dashed rounded-lg cursor-pointer hover:bg-gray-50 border-gray-300">
                  <div className="flex flex-col items-center justify-center pt-2 pb-2">
                    <Upload className="w-6 h-6 mb-1 text-gray-400" />
                    <p className="text-sm text-gray-500">Belge yüklemek için tıklayın</p>
                    <p className="text-xs text-gray-400">PDF, JPG, PNG, DOC</p>
                  </div>
                  <input
                    type="file"
                    className="hidden"
                    multiple
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                    onChange={handleFileUpload}
                    data-testid="request-file-input"
                  />
                </label>
              </div>
              {newRequest.documents.length > 0 && (
                <div className="mt-2 space-y-2">
                  {newRequest.documents.map((doc, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <File className="h-4 w-4 text-blue-500" />
                        <span className="text-sm truncate max-w-[200px]">{doc.name}</span>
                        <span className="text-xs text-gray-400">({(doc.size / 1024).toFixed(1)} KB)</span>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeDocument(index)}
                        className="text-red-500 hover:text-red-700 h-6 w-6 p-0"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateModalOpen(false)}>
              İptal
            </Button>
            <Button 
              onClick={handleCreateRequest}
              className="bg-[hsl(var(--brand-700))] hover:bg-[hsl(var(--brand-800))]"
              data-testid="submit-request-btn"
            >
              <Send className="h-4 w-4 mr-2" />
              Talep Oluştur
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Talep Detay Modalı */}
      <Dialog open={detailModalOpen} onOpenChange={setDetailModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5 text-[hsl(var(--brand-600))]" />
              Talep Detayı
            </DialogTitle>
          </DialogHeader>
          
          {selectedRequest && (
            <div className="space-y-4 max-h-[70vh] overflow-y-auto">
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Badge className="bg-[hsl(var(--brand-100))] text-[hsl(var(--brand-700))]">
                      {getCategoryLabel(selectedRequest.category)}
                    </Badge>
                    {getStatusBadge(selectedRequest.status)}
                  </div>
                  {selectedRequest.form_number && (
                    <span className="font-mono text-sm bg-white px-3 py-1 rounded border border-gray-200 text-gray-700">
                      {selectedRequest.form_number}
                    </span>
                  )}
                </div>
                <h3 className="font-bold text-lg mb-2">{selectedRequest.title}</h3>
              </div>
              
              {/* Açıklama Bölümü */}
              <div className="border rounded-lg p-4 bg-white">
                <Label className="text-gray-700 font-semibold mb-2 block">Açıklama</Label>
                <p className="text-gray-600 whitespace-pre-wrap">
                  {selectedRequest.description || 'Açıklama girilmemiş'}
                </p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">Öncelik</Label>
                  <div className="mt-1">{getPriorityBadge(selectedRequest.priority)}</div>
                </div>
                <div>
                  <Label className="text-gray-500">Oluşturulma</Label>
                  <p className="font-medium">
                    {new Date(selectedRequest.created_at).toLocaleDateString('tr-TR')}
                    <span className="text-gray-500 ml-2">
                      {new Date(selectedRequest.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </p>
                </div>
              </div>

              {/* İşlem Geçmişi */}
              {selectedRequest.action_history && selectedRequest.action_history.length > 0 && (
                <div className="border rounded-lg p-4 bg-white">
                  <Label className="text-gray-700 flex items-center gap-2 mb-3">
                    <History className="h-4 w-4" />
                    İşlem Geçmişi
                  </Label>
                  <div className="space-y-3">
                    {selectedRequest.action_history.map((action, index) => (
                      <div key={index} className="flex items-start gap-3">
                        <div className={`mt-1 w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                          action.action === 'created' ? 'bg-blue-500' :
                          action.action === 'approved' ? 'bg-green-500' :
                          action.action === 'rejected' ? 'bg-red-500' :
                          action.action === 'forwarded' ? 'bg-amber-500' :
                          'bg-gray-400'
                        }`}></div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-sm text-gray-900">
                              {action.action === 'created' ? 'Oluşturuldu' :
                               action.action === 'approved' ? 'Onaylandı' :
                               action.action === 'rejected' ? 'Reddedildi' :
                               action.action === 'forwarded' ? 'İletildi' :
                               action.action}
                            </span>
                            {action.target && (
                              <Badge variant="outline" className="text-xs">
                                <ArrowRight className="h-3 w-3 mr-1" />
                                {action.target === 'ik' ? 'İK' :
                                 action.target === 'muhasebe' ? 'Muhasebe' :
                                 action.target === 'satin_alma' ? 'Satın Alma' :
                                 action.target === 'idari_isler' ? 'İdari İşler' :
                                 action.target === 'yonetim' ? 'Yönetim' :
                                 action.target}
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                            <User className="h-3 w-3" />
                            <span>{action.user_name || 'Bilinmiyor'}</span>
                            <span>•</span>
                            <span>
                              {new Date(action.timestamp).toLocaleDateString('tr-TR')} {new Date(action.timestamp).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                          {action.note && (
                            <p className="text-sm text-gray-600 mt-1 bg-gray-50 p-2 rounded">{action.note}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedRequest.documents && selectedRequest.documents.length > 0 && (
                <div>
                  <Label className="text-gray-500">Yüklenen Belgeler ({selectedRequest.documents.length})</Label>
                  <div className="mt-2 space-y-2">
                    {selectedRequest.documents.map((doc, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-blue-100 rounded-lg">
                            <File className="h-5 w-5 text-blue-600" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-700">{doc.name}</p>
                            <p className="text-xs text-gray-400">{(doc.size / 1024).toFixed(1)} KB</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {doc.data && (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  // Yeni sekmede aç
                                  const newWindow = window.open();
                                  if (newWindow) {
                                    newWindow.document.write(`
                                      <html>
                                        <head><title>${doc.name}</title></head>
                                        <body style="margin:0;display:flex;justify-content:center;align-items:center;min-height:100vh;background:#f5f5f5;">
                                          ${doc.type?.startsWith('image/') 
                                            ? `<img src="${doc.data}" style="max-width:100%;max-height:100vh;" />`
                                            : doc.type === 'application/pdf'
                                              ? `<iframe src="${doc.data}" style="width:100%;height:100vh;border:none;"></iframe>`
                                              : `<div style="padding:20px;background:white;border-radius:8px;box-shadow:0 2px 10px rgba(0,0,0,0.1);"><p>Bu dosya türü önizlenemez.</p><p>Lütfen indirin.</p></div>`
                                          }
                                        </body>
                                      </html>
                                    `);
                                  }
                                }}
                                className="gap-1"
                                data-testid={`view-doc-${index}`}
                              >
                                <Eye className="h-4 w-4" />
                                Görüntüle
                              </Button>
                              <Button
                                variant="default"
                                size="sm"
                                onClick={() => {
                                  // İndir
                                  const link = document.createElement('a');
                                  link.href = doc.data;
                                  link.download = doc.name;
                                  document.body.appendChild(link);
                                  link.click();
                                  document.body.removeChild(link);
                                }}
                                className="gap-1 bg-blue-600 hover:bg-blue-700"
                                data-testid={`download-doc-${index}`}
                              >
                                <Download className="h-4 w-4" />
                                İndir
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Onay Bilgisi */}
              {selectedRequest.status === 'approved' && (
                <div className="p-4 rounded-lg border bg-green-50 border-green-200">
                  <Label className="text-green-700 flex items-center gap-2">
                    <CheckCircle className="h-4 w-4" />
                    Onay Bilgisi
                  </Label>
                  {selectedRequest.branch_approved_by_name && (
                    <p className="mt-1 text-sm text-gray-600">
                      <span className="font-medium">Onaylayan:</span> {selectedRequest.branch_approved_by_name}
                    </p>
                  )}
                  {selectedRequest.branch_approved_at && (
                    <p className="text-sm text-gray-600">
                      <span className="font-medium">Tarih:</span> {new Date(selectedRequest.branch_approved_at).toLocaleDateString('tr-TR')} {new Date(selectedRequest.branch_approved_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  )}
                  {selectedRequest.branch_approval_note && (
                    <div className="mt-2 p-2 bg-green-100 rounded">
                      <p className="text-sm font-medium text-green-800">Açıklama:</p>
                      <p className="text-green-700">{selectedRequest.branch_approval_note}</p>
                    </div>
                  )}
                  {selectedRequest.response_message && (
                    <p className="mt-2 text-green-600">{selectedRequest.response_message}</p>
                  )}
                </div>
              )}

              {/* Red Bilgisi */}
              {selectedRequest.status === 'rejected' && (
                <div className="p-4 rounded-lg border bg-red-50 border-red-200">
                  <Label className="text-red-700 flex items-center gap-2">
                    <XCircle className="h-4 w-4" />
                    Red Bilgisi
                  </Label>
                  {selectedRequest.branch_rejected_by_name && (
                    <p className="mt-1 text-sm text-gray-600">
                      <span className="font-medium">Reddeden:</span> {selectedRequest.branch_rejected_by_name}
                    </p>
                  )}
                  {selectedRequest.branch_rejected_at && (
                    <p className="text-sm text-gray-600">
                      <span className="font-medium">Tarih:</span> {new Date(selectedRequest.branch_rejected_at).toLocaleDateString('tr-TR')} {new Date(selectedRequest.branch_rejected_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  )}
                  {selectedRequest.branch_rejection_reason && (
                    <div className="mt-2 p-2 bg-red-100 rounded">
                      <p className="text-sm font-medium text-red-800">Red Nedeni:</p>
                      <p className="text-red-700">{selectedRequest.branch_rejection_reason}</p>
                    </div>
                  )}
                  {selectedRequest.response_message && (
                    <p className="mt-2 text-red-600">{selectedRequest.response_message}</p>
                  )}
                </div>
              )}

              {/* İletilme Bilgisi - Bekleyen */}
              {selectedRequest.status && selectedRequest.status.startsWith('forwarded_to_') && (
                <div className="p-4 rounded-lg border bg-amber-50 border-amber-200">
                  <Label className="text-amber-700 flex items-center gap-2">
                    <Send className="h-4 w-4" />
                    İletim Bilgisi
                  </Label>
                  <p className="mt-1 text-sm text-gray-600">
                    <span className="font-medium">Durum:</span> {
                      selectedRequest.status === 'forwarded_to_hr' ? "İK'ya iletildi - Onay bekleniyor" :
                      selectedRequest.status === 'forwarded_to_accounting' ? "Muhasebe'ye iletildi - Onay bekleniyor" :
                      selectedRequest.status === 'forwarded_to_purchasing' ? "Satın Alma'ya iletildi - Onay bekleniyor" :
                      selectedRequest.status === 'forwarded_to_admin' ? "İdari İşler'e iletildi - Onay bekleniyor" :
                      selectedRequest.status === 'forwarded_to_management' ? "Yönetime iletildi - Onay bekleniyor" :
                      "İletildi - Onay bekleniyor"
                    }
                  </p>
                  {selectedRequest.forwarded_at && (
                    <p className="text-sm text-gray-600">
                      <span className="font-medium">İletim Tarihi:</span> {new Date(selectedRequest.forwarded_at).toLocaleDateString('tr-TR')} {new Date(selectedRequest.forwarded_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  )}
                  {selectedRequest.forwarded_by_name && (
                    <p className="text-sm text-gray-600">
                      <span className="font-medium">İleten:</span> {selectedRequest.forwarded_by_name}
                    </p>
                  )}
                </div>
              )}

              {/* Departman Onay Bilgisi */}
              {selectedRequest.department_approval && (
                <div className="p-4 rounded-lg border bg-green-50 border-green-200">
                  <Label className="text-green-700 flex items-center gap-2">
                    <CheckCircle className="h-4 w-4" />
                    Departman Onayı
                  </Label>
                  <p className="mt-1 text-sm text-gray-600">
                    <span className="font-medium">Onaylayan:</span> {selectedRequest.department_approval.approved_by_name}
                  </p>
                  {selectedRequest.department_approval.timestamp && (
                    <p className="text-sm text-gray-600">
                      <span className="font-medium">Tarih:</span> {new Date(selectedRequest.department_approval.timestamp).toLocaleDateString('tr-TR')}
                    </p>
                  )}
                  {selectedRequest.department_approval.note && (
                    <div className="mt-2 p-2 bg-green-100 rounded">
                      <p className="text-sm text-green-700">{selectedRequest.department_approval.note}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Departman Red Bilgisi */}
              {selectedRequest.department_rejection && (
                <div className="p-4 rounded-lg border bg-red-50 border-red-200">
                  <Label className="text-red-700 flex items-center gap-2">
                    <XCircle className="h-4 w-4" />
                    Departman Reddi
                  </Label>
                  <p className="mt-1 text-sm text-gray-600">
                    <span className="font-medium">Reddeden:</span> {selectedRequest.department_rejection.rejected_by_name}
                  </p>
                  {selectedRequest.department_rejection.timestamp && (
                    <p className="text-sm text-gray-600">
                      <span className="font-medium">Tarih:</span> {new Date(selectedRequest.department_rejection.timestamp).toLocaleDateString('tr-TR')}
                    </p>
                  )}
                  {selectedRequest.department_rejection.reason && (
                    <div className="mt-2 p-2 bg-red-100 rounded">
                      <p className="text-sm font-medium text-red-800">Red Nedeni:</p>
                      <p className="text-red-700">{selectedRequest.department_rejection.reason}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Eski yanıt mesajı (diğer durumlar için) */}
              {selectedRequest.response_message && selectedRequest.status !== 'approved' && selectedRequest.status !== 'rejected' && (
                <div className="p-4 rounded-lg border bg-gray-50 border-gray-200">
                  <Label className="text-gray-700">Yanıt</Label>
                  <p className="mt-1 text-gray-600">{selectedRequest.response_message}</p>
                </div>
              )}
              
              {/* Talep Hareketi */}
              <RequestHistory request={selectedRequest} />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WorkRequests;
